/**
 * Services Library — org-wide catalogue of reusable interior
 * design SERVICES. Quote Step 2's "From Services Library"
 * picker reads from this collection.
 *
 * Layout mirrors `MaterialLibrary.tsx`:
 *   .projects-header — H1 "Services Library" + count stats + + Add
 *   .filter-bar      — All / per-category chips + local search
 *   .material-grid   — 4-col responsive cards (reuse iio.css class)
 *
 * Behaviour:
 *   - Live read via `useServiceLibrary(orgId)`
 *   - Category chip + search filter client-side
 *   - Click card → opens form slide-over in edit mode
 *   - `+ Add Service` opens it in create mode
 *
 * The create/edit form is inline in this file (smaller surface
 * than materials' since services have fewer category-specific
 * fields). Promote to a dedicated component if the form ever
 * grows past ~150 LOC.
 */
import { useEffect, useMemo, useState } from 'react';

import { canManageProjects } from '@shared/perms';

import { useTopbarSearch } from '@/components/chrome/TopbarSearchContext';
import { QUOTATION_UNITS } from '@/lib/quotations';
import {
  SERVICE_CATEGORIES,
  createService,
  deleteService,
  getServiceCategoryConfig,
  updateService,
  type ServiceCategory,
  type ServiceLibraryItem,
} from '@/lib/serviceLibrary';
import { useAuth } from '@/auth/AuthProvider';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import { useFullOrganization } from '@/lib/useFullOrganization';
import { useServiceLibrary } from '@/lib/useServiceLibrary';

export function ServiceLibraryPage() {
  const { org } = useFullOrganization();
  const orgId = org?.id ?? null;
  const { query: searchQuery, setPlaceholder } = useTopbarSearch();
  const [localSearch, setLocalSearch] = useState('');
  const [category, setCategory] = useState<ServiceCategory | null>(null);
  const [formMode, setFormMode] = useState<
    | { kind: 'closed' }
    | { kind: 'create' }
    | { kind: 'edit'; item: ServiceLibraryItem }
  >({ kind: 'closed' });
  const { role: orgRole } = useCurrentOrgRole();
  const canWrite = canManageProjects(orgRole);

  useEffect(() => {
    setPlaceholder('Search services by name, category, HSN…');
    return () => setPlaceholder('Search…');
  }, [setPlaceholder]);

  const effectiveSearch = (searchQuery || localSearch).trim();
  const { data, loading } = useServiceLibrary(orgId, effectiveSearch, category);

  const stats = useMemo(() => {
    const total = data.length;
    const cats = new Set<string>();
    for (const r of data) cats.add(r.category);
    return { total, categoryCount: cats.size };
  }, [data]);

  return (
    <div className="page active" id="page-services" style={{ display: 'block' }}>
      <div className="projects-header">
        <div className="projects-title-row">
          <h1>Services Library</h1>
          <div className="projects-stats">
            {stats.total} service{stats.total === 1 ? '' : 's'} ·{' '}
            {stats.categoryCount} categor
            {stats.categoryCount === 1 ? 'y' : 'ies'}
          </div>
        </div>
        {canWrite ? (
          // Hidden for roles that can't write — Firestore rule blocks it anyway
          <button
            type="button"
            className="btn-primary"
            onClick={() => setFormMode({ kind: 'create' })}
            disabled={!orgId}
          >
            <i className="ph-bold ph-plus" style={{ fontSize: 14 }} /> Add Service
          </button>
        ) : null}
      </div>

      {/* Filter bar — All + per-category chips + local search */}
      <div className="filter-bar">
        <button
          type="button"
          className={`filter-chip${category === null ? ' active' : ''}`}
          onClick={() => setCategory(null)}
        >
          All
        </button>
        {SERVICE_CATEGORIES.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`filter-chip${category === c.key ? ' active' : ''}`}
            onClick={() => setCategory(c.key)}
            title={c.label}
          >
            <i className={c.icon} /> {c.label}
          </button>
        ))}
        <div className="filter-sep" />
        <div className="filter-search-wrap" style={{ flex: 1 }}>
          <i className="ph ph-magnifying-glass" />
          <input
            className="filter-search"
            type="search"
            placeholder="Search services…"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.currentTarget.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          Loading library…
        </div>
      ) : data.length === 0 ? (
        <div className="card" style={{ padding: 48, textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>
            <i className="ph ph-wrench" />
          </div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {effectiveSearch || category
              ? 'No services match this filter'
              : 'Your services library is empty'}
          </div>
          <div
            style={{
              fontSize: 13,
              color: 'var(--text-secondary)',
              maxWidth: 460,
              margin: '0 auto 16px',
            }}
          >
            {effectiveSearch || category
              ? 'Try a different search term or clear the category filter.'
              : 'Add reusable services here — they show up as one-tap picks when you build a quotation. Brand identity design · 3D walkthroughs · site supervision · drawings …'}
          </div>
          {!effectiveSearch && !category && canWrite ? (
            // Hidden for roles that can't write — Firestore rule blocks it anyway
            <button
              type="button"
              className="btn-primary"
              onClick={() => setFormMode({ kind: 'create' })}
            >
              <i className="ph-bold ph-plus" /> Add your first service
            </button>
          ) : null}
        </div>
      ) : (
        <div className="material-grid">
          {data.map((s) => (
            <ServiceCard
              key={s.id}
              item={s}
              onClick={
                canWrite
                  ? () => setFormMode({ kind: 'edit', item: s })
                  : undefined
              }
            />
          ))}
        </div>
      )}

      <ServiceFormSlideOver
        open={formMode.kind !== 'closed'}
        onClose={() => setFormMode({ kind: 'closed' })}
        orgId={orgId}
        item={formMode.kind === 'edit' ? formMode.item : null}
      />
    </div>
  );
}

// ───── Card ─────────────────────────────────────────────────

function ServiceCard({
  item,
  onClick,
}: {
  item: ServiceLibraryItem;
  onClick?: () => void;
}) {
  const cfg = getServiceCategoryConfig(item.category);
  const clickable = !!onClick;
  return (
    <div
      className="material-card"
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter') onClick!();
            }
          : undefined
      }
      style={{ cursor: clickable ? 'pointer' : 'default', position: 'relative' }}
      title={clickable ? `Edit ${item.name}` : item.name}
    >
      <span
        className="stage-pill"
        style={{
          position: 'absolute',
          top: 10,
          right: 10,
          padding: '3px 8px',
          fontSize: 10,
          background: cfg.tintBg,
          color: cfg.color,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.4px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
          zIndex: 1,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <i className={cfg.icon} /> {cfg.label}
      </span>

      <div
        className="material-img"
        style={{
          background: cfg.tintBg,
          color: cfg.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 32,
        }}
      >
        <i className={cfg.icon} />
      </div>
      <div className="material-info">
        <div className="material-brand">{cfg.label}</div>
        <div className="material-title">{item.name}</div>
        {typeof item.defaultRate === 'number' && item.defaultRate > 0 ? (
          <div className="material-price">
            ₹{item.defaultRate.toLocaleString('en-IN')}{' '}
            <span className="material-unit">/ {item.unit}</span>
          </div>
        ) : (
          <div
            className="material-price"
            style={{ color: 'var(--text-tertiary)', fontWeight: 500 }}
          >
            <span style={{ fontSize: 11 }}>No default rate</span>{' '}
            <span className="material-unit">/ {item.unit}</span>
          </div>
        )}
        <div
          className="material-meta"
          style={{ justifyContent: 'flex-start' }}
        >
          <span className="material-sku">
            {item.description?.trim() ||
              (item.hsn ? `HSN ${item.hsn}` : 'No description')}
          </span>
        </div>
      </div>
    </div>
  );
}

// ───── Inline create/edit form (slide-over) ─────────────────

function ServiceFormSlideOver({
  open,
  onClose,
  orgId,
  item,
}: {
  open: boolean;
  onClose: () => void;
  orgId: string | null;
  item: ServiceLibraryItem | null;
}) {
  const { user } = useAuth();
  const { role: orgRole } = useCurrentOrgRole();
  const canWrite = canManageProjects(orgRole);
  const isEdit = !!item;

  const [name, setName] = useState('');
  const [category, setCategory] = useState<ServiceCategory>('design');
  const [description, setDescription] = useState('');
  const [unit, setUnit] = useState<string>('Nos');
  const [defaultRate, setDefaultRate] = useState('');
  const [hsn, setHsn] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Reset on open. When editing, pre-fill from the item.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setBusy(false);
    setConfirmingDelete(false);
    if (item) {
      setName(item.name);
      setCategory(item.category);
      setDescription(item.description ?? '');
      setUnit(item.unit);
      setDefaultRate(
        typeof item.defaultRate === 'number' && item.defaultRate > 0
          ? String(item.defaultRate)
          : '',
      );
      setHsn(item.hsn ?? '');
    } else {
      setName('');
      setCategory('design');
      setDescription('');
      setUnit('Nos');
      setDefaultRate('');
      setHsn('');
    }
  }, [open, item]);

  // Body scroll lock + Esc to close.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, busy, onClose]);

  const handleSubmit = async () => {
    setError(null);
    if (!orgId) return setError('No organisation in scope.');
    if (!user) return setError('You must be signed in.');
    if (!name.trim()) return setError('Service name is required.');
    setBusy(true);
    try {
      const rate = parseFloat(defaultRate) || 0;
      if (isEdit && item) {
        await updateService(item.id, {
          category,
          name: name.trim(),
          description,
          unit,
          defaultRate: rate,
          hsn,
        });
      } else {
        await createService({
          orgId,
          category,
          name: name.trim(),
          description: description.trim() || undefined,
          unit,
          defaultRate: rate > 0 ? rate : undefined,
          hsn: hsn.trim() || undefined,
          createdBy: user.uid,
        });
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save service.');
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!item) return;
    setBusy(true);
    try {
      await deleteService(item.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete.');
      setBusy(false);
    }
  };

  const cfg = getServiceCategoryConfig(category);

  return (
    <>
      <div
        className={`slide-overlay${open ? ' open' : ''}`}
        onClick={onClose}
        aria-hidden
      />
      <div
        className={`slide-over${open ? ' open' : ''}`}
        role="dialog"
        aria-label={isEdit ? 'Edit service' : 'Add service'}
        aria-hidden={!open}
      >
        <div className="slide-header">
          <h2 className="slide-title">
            {isEdit ? 'Edit service' : 'Add service'}
          </h2>
          <button
            type="button"
            className="slide-close"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="ph ph-x" />
          </button>
        </div>

        <div className="slide-body">
          {error ? (
            <div
              className="card"
              style={{
                padding: 12,
                background: 'var(--critical-bg)',
                color: 'var(--critical-text)',
                borderColor: 'var(--critical)',
                marginBottom: 12,
              }}
            >
              <i className="ph ph-warning-circle" /> {error}
            </div>
          ) : null}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              void handleSubmit();
            }}
          >
            {/* Category — chip picker */}
            <label
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: 'var(--text-tertiary)',
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                marginBottom: 8,
                display: 'block',
              }}
            >
              Category
            </label>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 6,
                marginBottom: 14,
              }}
            >
              {SERVICE_CATEGORIES.map((c) => {
                const active = c.key === category;
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => {
                      setCategory(c.key);
                      // Snap unit to the category's default if the
                      // user hasn't touched it yet (a Nos default
                      // service shouldn't suddenly default to Hrs
                      // mid-edit).
                      if (!isEdit) setUnit(c.defaultUnit);
                    }}
                    style={{
                      padding: '6px 10px',
                      borderRadius: 999,
                      border: active
                        ? `1px solid ${c.color}`
                        : '1px solid var(--border)',
                      background: active ? c.tintBg : '#fff',
                      color: active ? c.color : 'var(--text-secondary)',
                      fontSize: 12,
                      fontWeight: active ? 700 : 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <i className={c.icon} /> {c.label}
                  </button>
                );
              })}
            </div>

            {/* Name */}
            <FieldLabel text="Service name *" />
            <input
              required
              style={FIELD_INPUT}
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              placeholder={`e.g. ${cfg.label} — 2D layouts`}
              autoFocus
              maxLength={120}
            />

            {/* Description */}
            <FieldLabel text="Description" />
            <textarea
              style={{ ...FIELD_INPUT, minHeight: 64, resize: 'vertical' }}
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
              placeholder="Optional — scope, deliverables, what's included"
              rows={3}
            />

            <div style={{ display: 'flex', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <FieldLabel text="Unit *" />
                <select
                  style={FIELD_INPUT}
                  value={unit}
                  onChange={(e) => setUnit(e.currentTarget.value)}
                >
                  {QUOTATION_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <FieldLabel text="Default rate (₹)" />
                <input
                  type="number"
                  style={FIELD_INPUT}
                  value={defaultRate}
                  onChange={(e) => setDefaultRate(e.currentTarget.value)}
                  placeholder="Optional"
                  min={0}
                  step="any"
                />
              </div>
            </div>

            <FieldLabel text="HSN / SAC code" />
            <input
              style={FIELD_INPUT}
              value={hsn}
              onChange={(e) => setHsn(e.currentTarget.value)}
              placeholder="e.g. 998314 (Architectural services)"
              maxLength={20}
            />
            <div
              style={{
                fontSize: 11,
                color: 'var(--text-tertiary)',
                marginTop: 4,
                marginBottom: 14,
              }}
            >
              Auto-fills on every quote line picked from this service.
              Services typically use 998xxx SAC codes.
            </div>

            <div
              style={{
                display: 'flex',
                gap: 12,
                marginTop: 6,
                paddingTop: 16,
                borderTop: '1px solid var(--border)',
              }}
            >
              {canWrite && isEdit && !confirmingDelete ? (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setConfirmingDelete(true)}
                  disabled={busy}
                  style={{ color: 'var(--critical)' }}
                >
                  <i className="ph ph-trash" /> Delete
                </button>
              ) : null}
              {canWrite && confirmingDelete ? (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleDelete}
                  disabled={busy}
                  style={{
                    background: 'var(--critical)',
                    borderColor: 'var(--critical)',
                  }}
                >
                  {busy ? 'Deleting…' : 'Confirm delete'}
                </button>
              ) : null}
              {!canWrite ? (
                <span
                  style={{
                    fontSize: 12,
                    color: 'var(--text-tertiary)',
                    alignSelf: 'center',
                  }}
                >
                  Read-only
                </span>
              ) : null}
              <div style={{ flex: 1 }} />
              <button
                type="button"
                className="btn-secondary"
                onClick={onClose}
                disabled={busy}
              >
                {canWrite ? 'Cancel' : 'Close'}
              </button>
              {canWrite ? (
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={busy || !orgId || !name.trim()}
                >
                  {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Add service'}
                </button>
              ) : null}
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

const FIELD_INPUT: React.CSSProperties = {
  width: '100%',
  padding: 10,
  borderRadius: 6,
  border: '1px solid var(--border)',
  background: '#fff',
  fontSize: 13,
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
  outline: 'none',
};

function FieldLabel({ text }: { text: string }) {
  return (
    <label
      style={{
        display: 'block',
        fontSize: 11,
        fontWeight: 700,
        color: 'var(--text-tertiary)',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        marginTop: 12,
        marginBottom: 6,
      }}
    >
      {text}
    </label>
  );
}
